import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { useConstants } from '@/contexts/ConstantsContext';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, Phone, Users, Calendar, TrendingUp, RefreshCw, Star, CheckCircle, XCircle, UserCheck, User, MessageSquare, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, subtitle, icon: Icon, trend, onClick, clickable }) => (
  <Card className={`bg-card border-border ${clickable ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`} onClick={onClick}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 bg-primary/10 rounded-lg">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-4 flex items-center gap-1">
          <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
          <span className={`text-sm ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend}%
          </span>
        </div>
      )}
      {clickable && (
        <p className="text-xs text-primary mt-2">Click to view details →</p>
      )}
    </CardContent>
  </Card>
);

const Reports = () => {
  const navigate = useNavigate();
  const { users, industries, country_markets, pipeline_stages, sectors, getNichesForSector } = useConstants();
  const { isAdmin } = useAuth();

  const [filters, setFilters] = useState({
    start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    user_id: '',
    country_market: '',
    industry: '',
    sector: '',
    niche: '',
  });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const [callReport, setCallReport] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [appointmentReport, setAppointmentReport] = useState(null);
  const [qualityReport, setQualityReport] = useState(null);
  const [dmReport, setDmReport] = useState(null);
  const [dmActivities, setDmActivities] = useState([]);
  const [dmDrilldownOpen, setDmDrilldownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Get niches for selected sector
  const availableNiches = filters.sector ? getNichesForSector(filters.sector) : [];

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const promises = [
        axios.post(`${API}/reports/calls`, filters),
        axios.post(`${API}/reports/pipeline`, filters),
        axios.post(`${API}/reports/appointments`, filters),
      ];
      
      // Admin-only reports
      if (isAdmin) {
        promises.push(axios.post(`${API}/reports/quality`, filters));
        promises.push(axios.post(`${API}/reports/decision-maker`, filters));
      }
      
      const results = await Promise.all(promises);
      setCallReport(results[0].data);
      setPipelineReport(results[1].data);
      setAppointmentReport(results[2].data);
      if (isAdmin) {
        if (results[3]) setQualityReport(results[3].data);
        if (results[4]) setDmReport(results[4].data);
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchDmDrilldown = async () => {
    try {
      const response = await axios.post(`${API}/reports/decision-maker/activities`, filters);
      setDmActivities(response.data);
      setDmDrilldownOpen(true);
    } catch (error) {
      toast.error('Failed to load DM activities');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchReports();
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'week':
        start = subDays(today, 7);
        end = today;
        break;
      case 'month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case '30days':
        start = subDays(today, 30);
        end = today;
        break;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      start_date: format(start, 'yyyy-MM-dd'),
      end_date: format(end, 'yyyy-MM-dd'),
    }));
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1">Call metrics and pipeline analytics</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Date Presets */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setPresetRange('today')} className="border-border">
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPresetRange('week')} className="border-border">
              Last 7 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPresetRange('30days')} className="border-border">
              Last 30 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPresetRange('month')} className="border-border">
              This Month
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-border">
                    <Calendar className="w-4 h-4 mr-2" />
                    {filters.start_date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <CalendarPicker
                    mode="single"
                    selected={filters.start_date ? new Date(filters.start_date) : undefined}
                    onSelect={(d) => {
                      if (d) handleFilterChange('start_date', format(d, 'yyyy-MM-dd'));
                      setStartDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-border">
                    <Calendar className="w-4 h-4 mr-2" />
                    {filters.end_date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <CalendarPicker
                    mode="single"
                    selected={filters.end_date ? new Date(filters.end_date) : undefined}
                    onSelect={(d) => {
                      if (d) handleFilterChange('end_date', format(d, 'yyyy-MM-dd'));
                      setEndDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* User */}
            <div className="space-y-2">
              <Label>User</Label>
              <Select value={filters.user_id || "all"} onValueChange={(v) => handleFilterChange('user_id', v === "all" ? "" : v)}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Market */}
            <div className="space-y-2">
              <Label>Market</Label>
              <Select value={filters.country_market || "all"} onValueChange={(v) => handleFilterChange('country_market', v === "all" ? "" : v)}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue placeholder="All markets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All markets</SelectItem>
                  {country_markets.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Industry */}
            <div className="space-y-2">
              <Label>Industry</Label>
              <Select value={filters.industry || "all"} onValueChange={(v) => handleFilterChange('industry', v === "all" ? "" : v)}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue placeholder="All industries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All industries</SelectItem>
                  {industries.map(i => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sector (Admin only - for DM reports) */}
            {isAdmin && (
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select 
                  value={filters.sector || "all"} 
                  onValueChange={(v) => {
                    handleFilterChange('sector', v === "all" ? "" : v);
                    handleFilterChange('niche', ''); // Reset niche when sector changes
                  }}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="All sectors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sectors</SelectItem>
                    {sectors.map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Niche (Admin only - for DM reports) */}
            {isAdmin && filters.sector && (
              <div className="space-y-2">
                <Label>Niche</Label>
                <Select value={filters.niche || "all"} onValueChange={(v) => handleFilterChange('niche', v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="All niches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All niches</SelectItem>
                    {availableNiches.map(n => (
                      <SelectItem key={n.id} value={n.name}>{n.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button onClick={applyFilters} data-testid="apply-filters">
            <RefreshCw className="w-4 h-4 mr-2" />
            Apply Filters
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Call Metrics */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Call Metrics</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Total Calls"
                value={callReport?.total_calls || 0}
                icon={Phone}
              />
              <StatCard
                title="Pick Ups"
                value={callReport?.pickups || 0}
                subtitle={`${callReport?.pickup_rate || 0}% rate`}
                icon={Phone}
              />
              <StatCard
                title="DM Conversations"
                value={callReport?.dm_conversations || 0}
                subtitle={`${callReport?.dm_rate || 0}% of pickups`}
                icon={Users}
              />
              <StatCard
                title="Appointments Booked"
                value={callReport?.appointments_scheduled || 0}
                subtitle={`${callReport?.booking_rate || 0}% booking rate`}
                icon={Calendar}
              />
            </div>
          </div>

          {/* Conversion Funnel */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Conversion Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-32 text-sm text-muted-foreground">Total Calls</div>
                  <div className="flex-1 bg-secondary rounded-full h-8 overflow-hidden">
                    <div className="bg-primary h-full" style={{ width: '100%' }} />
                  </div>
                  <div className="w-16 text-right font-mono">{callReport?.total_calls || 0}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 text-sm text-muted-foreground">Pick Ups</div>
                  <div className="flex-1 bg-secondary rounded-full h-8 overflow-hidden">
                    <div 
                      className="bg-blue-500 h-full" 
                      style={{ width: `${callReport?.pickup_rate || 0}%` }} 
                    />
                  </div>
                  <div className="w-16 text-right font-mono">{callReport?.pickups || 0}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 text-sm text-muted-foreground">DM Convos</div>
                  <div className="flex-1 bg-secondary rounded-full h-8 overflow-hidden">
                    <div 
                      className="bg-violet-500 h-full" 
                      style={{ width: `${callReport?.total_calls > 0 ? (callReport?.dm_conversations / callReport?.total_calls * 100) : 0}%` }} 
                    />
                  </div>
                  <div className="w-16 text-right font-mono">{callReport?.dm_conversations || 0}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 text-sm text-muted-foreground">Appointments</div>
                  <div className="flex-1 bg-secondary rounded-full h-8 overflow-hidden">
                    <div 
                      className="bg-emerald-500 h-full" 
                      style={{ width: `${callReport?.total_calls > 0 ? (callReport?.appointments_scheduled / callReport?.total_calls * 100) : 0}%` }} 
                    />
                  </div>
                  <div className="w-16 text-right font-mono">{callReport?.appointments_scheduled || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Metrics */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Pipeline Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <StatCard
                title="Total Leads"
                value={pipelineReport?.total_leads || 0}
                icon={Users}
              />
              <StatCard
                title="Total Pipeline Value"
                value={`£${(pipelineReport?.total_value || 0).toLocaleString()}`}
                icon={BarChart3}
              />
            </div>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Leads by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pipeline_stages.map(stage => {
                    const count = pipelineReport?.counts?.[stage] || 0;
                    const value = pipelineReport?.values?.[stage] || 0;
                    const maxCount = Math.max(...Object.values(pipelineReport?.counts || {}), 1);
                    
                    return (
                      <div key={stage} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span>{stage}</span>
                          <span className="text-muted-foreground">
                            {count} leads • £{value.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${(count / maxCount) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Appointment Metrics */}
          {appointmentReport && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Appointment Metrics</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{appointmentReport.totals?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Appts</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{appointmentReport.totals?.scheduled || 0}</p>
                    <p className="text-xs text-muted-foreground">Scheduled</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border border-emerald-500/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{appointmentReport.totals?.attended || 0}</p>
                    <p className="text-xs text-muted-foreground">Attended</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border border-red-500/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">{appointmentReport.totals?.no_show || 0}</p>
                    <p className="text-xs text-muted-foreground">No Shows</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{appointmentReport.totals?.rescheduled || 0}</p>
                    <p className="text-xs text-muted-foreground">Rescheduled</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{appointmentReport.totals?.cancelled || 0}</p>
                    <p className="text-xs text-muted-foreground">Cancelled</p>
                  </CardContent>
                </Card>
              </div>

              {/* Per-user appointment breakdown */}
              {appointmentReport.by_user?.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <UserCheck className="w-5 h-5" />
                      Appointments by User (Booked By)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-2">User</th>
                            <th className="text-center py-2 px-2">Scheduled</th>
                            <th className="text-center py-2 px-2">Attended</th>
                            <th className="text-center py-2 px-2">No Show</th>
                            <th className="text-center py-2 px-2">Rescheduled</th>
                            <th className="text-center py-2 px-2">Cancelled</th>
                            <th className="text-center py-2 px-2">Show Rate</th>
                            <th className="text-center py-2 px-2">No Show Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointmentReport.by_user.map(u => (
                            <tr key={u.user_id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-2 px-2 font-medium">{u.user_name}</td>
                              <td className="text-center py-2 px-2">{u.scheduled}</td>
                              <td className="text-center py-2 px-2 text-emerald-400">{u.attended}</td>
                              <td className="text-center py-2 px-2 text-red-400">{u.no_show}</td>
                              <td className="text-center py-2 px-2">{u.rescheduled}</td>
                              <td className="text-center py-2 px-2">{u.cancelled}</td>
                              <td className="text-center py-2 px-2">
                                <Badge variant="outline" className={u.show_rate >= 70 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : ''}>
                                  {u.show_rate}%
                                </Badge>
                              </td>
                              <td className="text-center py-2 px-2">
                                <Badge variant="outline" className={u.no_show_rate >= 30 ? 'bg-red-500/10 text-red-400 border-red-500/30' : ''}>
                                  {u.no_show_rate}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Quality Metrics (Admin Only) */}
          {isAdmin && qualityReport && (
            <div>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" />
                Quality Metrics
                <Badge variant="outline" className="text-xs">Admin Only</Badge>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{qualityReport.totals?.total_scored || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Scored</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{qualityReport.totals?.avg_quality_score || 0}</p>
                    <p className="text-xs text-muted-foreground">Avg Quality Score</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border border-emerald-500/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{qualityReport.totals?.good_fit_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Good Fits</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border border-red-500/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">{qualityReport.totals?.not_fit_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Not Fits</p>
                  </CardContent>
                </Card>
              </div>

              {/* Per-user quality breakdown */}
              {qualityReport.by_user?.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg">Quality by User (Who Booked)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-2">User</th>
                            <th className="text-center py-2 px-2">Scored</th>
                            <th className="text-center py-2 px-2">Avg Score</th>
                            <th className="text-center py-2 px-2">Good Fits</th>
                            <th className="text-center py-2 px-2">Not Fits</th>
                            <th className="text-center py-2 px-2">Good Fit Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {qualityReport.by_user.map(u => (
                            <tr key={u.user_id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-2 px-2 font-medium">{u.user_name}</td>
                              <td className="text-center py-2 px-2">{u.total_scored}</td>
                              <td className="text-center py-2 px-2">
                                <Badge variant="outline" className={u.avg_quality_score >= 4 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : u.avg_quality_score >= 3 ? '' : 'bg-red-500/10 text-red-400 border-red-500/30'}>
                                  {u.avg_quality_score}
                                </Badge>
                              </td>
                              <td className="text-center py-2 px-2 text-emerald-400">{u.good_fit_count}</td>
                              <td className="text-center py-2 px-2 text-red-400">{u.not_fit_count}</td>
                              <td className="text-center py-2 px-2">
                                <Badge variant="outline" className={u.good_fit_rate >= 70 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : ''}>
                                  {u.good_fit_rate}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Decision Maker Reports (Admin Only) */}
          {isAdmin && dmReport && (
            <div data-testid="dm-reports-section">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Decision Maker Contact
                <Badge variant="outline" className="text-xs">Admin Only</Badge>
              </h2>
              
              {/* DM KPIs */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card 
                  className="bg-card border-border border-blue-500/30 cursor-pointer hover:border-blue-400/50 transition-colors"
                  onClick={fetchDmDrilldown}
                  data-testid="dm-reached-kpi"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-500/10 rounded-lg">
                        <User className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-400">{dmReport.totals?.dm_reached || 0}</p>
                        <p className="text-xs text-muted-foreground">Decision Maker Reached</p>
                        <p className="text-xs text-blue-400 mt-1">reached_who = "Decision maker"</p>
                      </div>
                    </div>
                    <p className="text-xs text-primary mt-2">Click to view activities →</p>
                  </CardContent>
                </Card>
                
                <Card 
                  className="bg-card border-border border-emerald-500/30 cursor-pointer hover:border-emerald-400/50 transition-colors"
                  onClick={fetchDmDrilldown}
                  data-testid="spoke-to-dm-kpi"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-emerald-500/10 rounded-lg">
                        <MessageSquare className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-400">{dmReport.totals?.spoke_to_dm || 0}</p>
                        <p className="text-xs text-muted-foreground">Spoke to Decision Maker</p>
                        <p className="text-xs text-emerald-400 mt-1">Qualified conversation</p>
                      </div>
                    </div>
                    <p className="text-xs text-primary mt-2">Click to view activities →</p>
                  </CardContent>
                </Card>
              </div>

              {/* DM by User breakdown */}
              {dmReport.by_user?.length > 0 && (
                <Card className="bg-card border-border mb-4">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Decision Maker Contact by User
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-2">User</th>
                            <th className="text-center py-2 px-2">DM Reached</th>
                            <th className="text-center py-2 px-2">Spoke to DM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dmReport.by_user.map(u => (
                            <tr key={u.user_id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-2 px-2 font-medium">{u.user_name}</td>
                              <td className="text-center py-2 px-2 text-blue-400">{u.dm_reached}</td>
                              <td className="text-center py-2 px-2 text-emerald-400">{u.spoke_to_dm}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* DM Activities Drilldown */}
              <Collapsible open={dmDrilldownOpen} onOpenChange={setDmDrilldownOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full border-border mb-2" data-testid="dm-drilldown-toggle">
                    {dmDrilldownOpen ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                    {dmDrilldownOpen ? 'Hide' : 'Show'} DM Activities ({dmActivities.length})
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {dmActivities.length > 0 ? (
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-lg">Decision Maker Activities</CardTitle>
                        <CardDescription>
                          Activities where reached_who = "Decision maker" or decision_maker_status = "Spoke to decision maker"
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[400px]">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-card">
                              <tr className="border-b border-border">
                                <th className="text-left py-2 px-2">Date/Time</th>
                                <th className="text-left py-2 px-2">Lead</th>
                                <th className="text-left py-2 px-2">Logged By</th>
                                <th className="text-left py-2 px-2">Type</th>
                                <th className="text-left py-2 px-2">Outcome</th>
                                <th className="text-left py-2 px-2">Reached Who</th>
                                <th className="text-left py-2 px-2">DM Status</th>
                                <th className="text-left py-2 px-2">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dmActivities.map((activity, idx) => (
                                <tr key={activity.id || idx} className="border-b border-border/50 hover:bg-secondary/30">
                                  <td className="py-2 px-2 text-xs">
                                    {activity.created_at ? format(new Date(activity.created_at), 'MMM d, h:mm a') : '-'}
                                  </td>
                                  <td className="py-2 px-2">
                                    <button
                                      onClick={() => navigate(`/leads/${activity.lead_id}`)}
                                      className="text-primary hover:underline flex items-center gap-1"
                                    >
                                      <span className="font-medium">{activity.company_name}</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </button>
                                    <p className="text-xs text-muted-foreground">{activity.lead_name}</p>
                                  </td>
                                  <td className="py-2 px-2">{activity.logged_by}</td>
                                  <td className="py-2 px-2">
                                    <Badge variant="secondary" className="text-xs">{activity.activity_type}</Badge>
                                  </td>
                                  <td className="py-2 px-2">{activity.call_outcome || '-'}</td>
                                  <td className="py-2 px-2">
                                    {activity.reached_who === 'Decision maker' ? (
                                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">Decision maker</Badge>
                                    ) : (
                                      <span className="text-muted-foreground">{activity.reached_who || '-'}</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2">
                                    {activity.decision_maker_status === 'Spoke to decision maker' ? (
                                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Spoke to DM</Badge>
                                    ) : (
                                      <span className="text-muted-foreground">{activity.decision_maker_status || '-'}</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-xs text-muted-foreground max-w-[200px] truncate">
                                    {activity.notes || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-card border-border">
                      <CardContent className="p-8 text-center text-muted-foreground">
                        No DM activities found for the selected filters. Click one of the KPI cards to load activities.
                      </CardContent>
                    </Card>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
